/**
 * Ambulance Order Review Page
 *
 * Review interface for ambulance orders. Allows reviewers to:
 * - View pending orders in chronological order
 * - Filter by status, date range, cost center, patient name
 * - Mark orders as reviewed with optional comment
 * - See which orders have comments (speech bubble icon)
 * - Click to view full order details and change history
 */
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  CheckCircle,
  Clock,
  MessageCircle,
  X,
  Eye,
  AlertCircle,
} from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useOrganization } from '../contexts/OrganizationContext'
import type {
  AmbulanceOrderListItem,
  AmbulanceOrderListResponse,
} from '../types/ambulance'

interface CostCenter {
  id: string
  code: string
  name: string
}

interface UserInfo {
  role: string
  can_review_ambulance?: boolean
}

export default function AmbulanceOrderReviewPage() {
  const { t } = useTranslation('ambulance')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedOrgId } = useOrganization()

  // Fetch current user info
  const { data: userInfo } = useQuery<UserInfo>({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.get('/auth/me'),
    staleTime: 5 * 60 * 1000,
  })

  // Filters state
  const [showReviewed, setShowReviewed] = useState(false)
  const [costCenterId, setCostCenterId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [patientName, setPatientName] = useState('')

  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [reviewComment, setReviewComment] = useState('')

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {}
    if (selectedOrgId) params.organization_id = selectedOrgId
    if (!showReviewed) params.status = 'pending'
    if (costCenterId) params.cost_center_id = costCenterId
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    if (patientName) params.patient_name = patientName
    return params
  }, [selectedOrgId, showReviewed, costCenterId, dateFrom, dateTo, patientName])

  // Fetch orders
  const { data: ordersData, isLoading: ordersLoading, error: ordersError } = useQuery<AmbulanceOrderListResponse>({
    queryKey: ['ambulance-orders-review', queryParams],
    queryFn: () => {
      const searchParams = new URLSearchParams(queryParams)
      return apiClient.get(`/ambulance-orders?${searchParams.toString()}`)
    },
  })

  // Fetch cost centers for filter
  const { data: costCentersData } = useQuery<{ data: CostCenter[] }>({
    queryKey: ['cost-centers', selectedOrgId],
    queryFn: () => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.get(`/cost-centers${params}`)
    },
  })

  // Review mutation
  const reviewMutation = useMutation({
    mutationFn: (data: { orderId: string; comment?: string }) => {
      const params = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      return apiClient.post(`/ambulance-orders/${data.orderId}/review${params}`, {
        comment: data.comment || null,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambulance-orders-review'] })
      setReviewDialogOpen(false)
      setSelectedOrderId(null)
      setReviewComment('')
    },
  })

  const handleOpenReviewDialog = (orderId: string) => {
    setSelectedOrderId(orderId)
    setReviewComment('')
    setReviewDialogOpen(true)
  }

  const handleReview = () => {
    if (!selectedOrderId) return
    reviewMutation.mutate({
      orderId: selectedOrderId,
      comment: reviewComment || undefined,
    })
  }

  const handleClearFilters = () => {
    setCostCenterId('')
    setDateFrom('')
    setDateTo('')
    setPatientName('')
  }

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('fi-FI')
  const formatTime = (timeStr: string) => timeStr.substring(0, 5)

  // Check if user has reviewer permission
  // Only system_admin has automatic access, organization_admin needs explicit permission
  const isSystemAdmin = userInfo?.role === 'system_admin'
  const canReview = isSystemAdmin || userInfo?.can_review_ambulance

  if (!canReview) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {t('errors.noReviewPermission')}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('review.title')}</h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Show reviewed toggle */}
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showReviewed}
              onChange={(e) => setShowReviewed(e.target.checked)}
              className="mr-2"
            />
            {t('review.showReviewed')}
          </label>

          {/* Cost center filter */}
          <select
            value={costCenterId}
            onChange={(e) => setCostCenterId(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">{t('review.allCostCenters')}</option>
            {costCentersData?.data.map((cc) => (
              <option key={cc.id} value={cc.id}>
                {cc.code} - {cc.name}
              </option>
            ))}
          </select>

          {/* Date from */}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder={t('review.dateFrom')}
            className="border rounded-lg px-3 py-2"
          />

          {/* Date to */}
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder={t('review.dateTo')}
            className="border rounded-lg px-3 py-2"
          />

          {/* Patient name search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              placeholder={t('review.searchPatientName')}
              className="w-full border rounded-lg pl-10 pr-3 py-2"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={handleClearFilters}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            {t('review.clearFilters')}
          </button>
        </div>
      </div>

      {/* Error display */}
      {ordersError && (
        <div className="p-4 bg-red-100 text-red-700 rounded-lg mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {t('errors.loadFailed')}
        </div>
      )}

      {/* Orders table */}
      <div className="bg-white rounded-lg shadow">
        {ordersLoading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : ordersData?.data.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {showReviewed ? t('review.noOrders') : t('review.noPendingOrders')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.pickupDate')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.pickupTime')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.orderType')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.ordererName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.patientName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.costCenter')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('list.status')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    {t('list.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ordersData?.data.map((order: AmbulanceOrderListItem) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/ambulance-orders/${order.id}`)}
                  >
                    <td className="px-4 py-3 text-sm">{formatDate(order.pickup_date)}</td>
                    <td className="px-4 py-3 text-sm">{formatTime(order.pickup_time)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.order_type === 'emergency'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {t(`orderType.${order.order_type}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{order.orderer_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="flex items-center gap-1">
                        {order.patient_name}
                        {order.has_comments && (
                          <span title={t('list.hasComments')}>
                            <MessageCircle className="w-4 h-4 text-blue-500" />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{order.cost_center_name || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`flex items-center gap-1 ${
                        order.status === 'reviewed' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {order.status === 'reviewed' ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            {t('status.reviewed')}
                          </>
                        ) : (
                          <>
                            <Clock className="w-4 h-4" />
                            {t('status.pending')}
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/ambulance-orders/${order.id}`)
                          }}
                          className="p-1 text-gray-600 hover:text-gray-800"
                          title={t('list.viewOrder')}
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        {order.status === 'pending' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenReviewDialog(order.id)
                            }}
                            className="p-1 text-green-600 hover:text-green-800"
                            title={t('review.markAsReviewed')}
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review confirmation dialog */}
      {reviewDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{t('review.confirmTitle')}</h3>
              <button onClick={() => setReviewDialogOpen(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">{t('review.confirmMessage')}</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('review.comment')}
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder={t('review.commentPlaceholder')}
                rows={3}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {reviewMutation.isError && (
              <div className="p-3 bg-red-100 text-red-700 rounded-lg mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                {t('errors.reviewFailed')}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReviewDialogOpen(false)}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReview}
                disabled={reviewMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('review.markAsReviewed')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
